import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  StatusBadge,
  ApproverRoleBadge,
  DarCaseHistorySection,
  ClarificationThread,
  EmployeeSearchInput,
  useConfirm,
  WarningBanner,
} from "./shared/components";
import { ResignationFormSections } from "./shared/ResignationFormSections";
import { NoDuesFormSections } from "./shared/NoDuesFormSections";
import {
  getResignationDetails,
  sendToVigilance,
  hrUpdateResignationRequest,
  getDARClearanceRemarks,
  generateAcceptanceLetter,
  generateServiceCertificate,
  generateRelievingOrder,
  generateNoDuesCertificate,
  getNoDuesDispatchList,
  NoDuesDispatchRow,
  getNoDuesFormDetails,
  submitApprovalAction,
  raiseClarification,
  getEmployeeDetails,
  formatINR,
  formatDateLong,
  formatDate,
  VigilanceCaseRecord,
  DARRemarks,
  DarDetails,
  EmployeeDetails,
  getCurrentEmpNo,
  getHrLevelConfig,
  resolveHrRoster,
  submitSendToCA,
} from "./services/eSeparation.service";

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return formatDate(v);
  }
  return String(v);
};

const toDateInput = (v: any): string => {
  if (!v) return "";
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
};

// The applicant's own entry has no approver role, so the history falls back to
// "Level <n>" (e.g. "Level 2"). Show it as "Employee" in the DAR summary.
const formatDarLevel = (level?: string): string => {
  const l = String(level || "").trim();
  if (/^level\s*2$/i.test(l)) return "Employee";
  return l || "—";
};

// HR levels only ever forward or ask for clarification — rejection authority
// does not sit with HR at any of the 4 levels.
type Recommendation = "forward" | "clarify" | "";

const HRVerificationDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;

  const state = (location.state as any) || {};
  const requestId: string = state.request_id || params.id || "";

  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<any>(null);
  const [empMaster, setEmpMaster] = useState<EmployeeDetails | null>(null);
  const [vigilanceCase, setVigilanceCase] = useState<VigilanceCaseRecord | null>(null);
  const [sendingToVigilance, setSendingToVigilance] = useState(false);
  const [dar, setDar] = useState<DARRemarks[]>([]);

  const [hrRemarks, setHrRemarks] = useState("");
  const [recommendation, setRecommendation] = useState<Recommendation>("");
  const [submitting, setSubmitting] = useState(false);

  // Document downloads (Acceptance Letter / Service Certificate / Relieving
  // Order / No-Dues Settlement) — availability gated by isCaApproved /
  // isHrAdminApproved below.
  const [nodDuesRequestId, setNodDuesRequestId] = useState<string | null>(null);
  // True once the linked No-Dues child request itself is APPROVED (all
  // departments cleared / settlement closed) — distinct from nodDuesRequestId
  // above, which only tracks whether a No-Dues has been raised at all. Feeds
  // canGenerateFinalDocs' 3-condition check for V2 requests.
  const [noDuesSettled, setNoDuesSettled] = useState(false);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  // The employee's submitted No-Dues form payload (esep_no_dues.details) —
  // rendered via the shared NoDuesFormSections, identical to what the
  // employee saw and filled in on their own No-Dues form.
  const [ndForm, setNdForm] = useState<any>(null);

  // Read-only, real-time No-Dues department status — every HR level (not just
  // the Sr. CRA who dispatches it) can see this the moment it's raised, since
  // only the current HR step's own occupant can act on the No-Dues itself.
  const [noDuesDispatchRows, setNoDuesDispatchRows] = useState<NoDuesDispatchRow[]>([]);

  // V2 only: the "Send to Competent Authority" action is restricted to the
  // configured HOD HR (level 4) for this request's emp_group — fetched once so
  // we can tell whether the current viewer holds that seat.
  const [hodHrEmpId, setHodHrEmpId] = useState<string | null>(null);
  const [sendingToCA, setSendingToCA] = useState(false);
  // The HOD HR may send the request to a different Competent Authority than the
  // configured one — searched by employee number / name.
  const [changeCA, setChangeCA] = useState(false);
  const [caQuery, setCaQuery] = useState("");
  const [caPick, setCaPick] = useState<{ emp_no: string; emp_name: string; designation?: string } | null>(null);

  // HR edit mode — every field on this screen becomes editable, gated to
  // before HR forwards the request to the Competent Authority.
  const [editMode, setEditMode] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  // For a short notice: the ORIGINAL resignation, whose form is what HR sees.
  const [origRecord, setOrigRecord] = useState<any>(null);

  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getResignationDetails(requestId)
      .then((res) => {
        if (!active) return;
        const data = res.data || null;
        setDetails(data);
        setVigilanceCase(data?.vigilance_case ?? null);
        if (String(data?.request_type ?? "").toUpperCase() === "SHORT_NOTICE" && data?.orig_request_id) {
          getResignationDetails(data.orig_request_id).then((o) => {
            if (active) setOrigRecord(o.data || null);
          });
        }
        const empNo = data?.emp_no || state.emp_no;
        if (empNo) {
          // Live SAP master-data lookup — the only source for fields never
          // persisted onto the resignation record itself (Place of Posting,
          // Basic Pay, Home Address), used read-only in ResignationFormSections.
          getEmployeeDetails(empNo).then((emp) => {
            if (active && emp.data) setEmpMaster(emp.data);
          });
        }
        getDARClearanceRemarks(requestId).then((r) => {
          if (active) setDar(r.data || []);
        });
        getNoDuesFormDetails(requestId).then((nd) => {
          const ndReqId = nd.data?.nodues_request_id || null;
          if (active) setNodDuesRequestId(ndReqId);
          if (active) setNdForm(nd.data?.details || null);
          if (data?.request_type === "DEATH") {
            // A death case's No-Dues is auto-cleared — never blocks finalization.
            if (active) setNoDuesSettled(true);
          } else if (ndReqId) {
            getResignationDetails(ndReqId).then((ndDetail) => {
              if (active) {
                setNoDuesSettled(
                  String(ndDetail.data?.status || "").toUpperCase() === "APPROVED"
                );
              }
            });
          }
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const d = details || {};
  const isShortNotice = String(d.request_type || "").toUpperCase() === "SHORT_NOTICE";
  // A short notice is shown exactly like the original resignation form: every
  // detail comes from the ORIGINAL resignation; only the short-notice specifics
  // (new relieving date, shortfall, amount) come from the short notice itself.
  const formRecord =
    isShortNotice && origRecord
      ? {
          ...origRecord,
          early_relieve: "Y",
          desired_relieve_date: d.desired_relieve_date,
          shortfall_days: d.shortfall_days,
          short_notice_amount: d.short_notice_amount,
        }
      : d;

  useEffect(() => {
    if (String(d.workflow_version || "V1").toUpperCase() !== "V2" || !d.emp_group) return;
    let active = true;
    // The HOD HR depends on the roster the employee's wing + group fall under.
    const roster = resolveHrRoster(d.wing, d.emp_group);
    getHrLevelConfig().then((res) => {
      if (!active) return;
      const level4 = (res.data || []).find(
        (r) => r.roster === roster && r.level_no === 4
      );
      setHodHrEmpId(level4?.approver_emp_id || null);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.workflow_version, d.emp_group, d.wing]);

  useEffect(() => {
    if (!nodDuesRequestId) {
      setNoDuesDispatchRows([]);
      return;
    }
    let active = true;
    getNoDuesDispatchList(nodDuesRequestId).then((res) => {
      if (active) setNoDuesDispatchRows(res.data || []);
    });
    return () => {
      active = false;
    };
  }, [nodDuesRequestId]);

  const bondData = useMemo(
    () => ({
      under_bond: d.is_bond === "Y" || d.is_bond === true,
      bond_period: d.bond_details || "",
      training_expenditure: Number(d.training_bond_amount) || 0,
      pending_recoveries: Number(d.payable_amount) || 0,
    }),
    [d]
  );

  // HR can no longer edit once the Competent Authority has acted or the case
  // is finalized — mirrors the backend's HR_EDIT_BLOCKED_STATUSES gate.
  const EDIT_BLOCKED_STATUSES = new Set(["CA_APPROVED", "APPROVED", "REJECTED", "WITHDRAWN"]);
  // (A short notice's form details belong to the original resignation, so they
  // are not editable here.)
  const canEdit = !isShortNotice && !EDIT_BLOCKED_STATUSES.has(String(d.status || "").toUpperCase());

  // V2 requests carry an explicit d.ca_approval — prefer it. Falls back to the
  // legacy status/steps heuristic for V1 requests (ca_approval is always null
  // there).
  const isCaApproved = useMemo(() => {
    if (d.ca_approval) return String(d.ca_approval.status || "").toUpperCase() === "APPROVED";
    const s = String(d.status || "").toUpperCase();
    if (["CA_APPROVED", "APPROVED", "COMPLETED", "CLOSED", "DEPT_CLEARANCE", "NO_DUES_INITIATED"].includes(s)) {
      return true;
    }
    if (Array.isArray(d.steps)) {
      const caStep = d.steps.find((st: any) =>
        String(st.role || st.step_name || "").toUpperCase().includes("CA") ||
        String(st.role || st.step_name || "").toUpperCase().includes("COMPETENT")
      );
      if (caStep && String(caStep.status || "").toUpperCase() === "APPROVED") {
        return true;
      }
    }
    return false;
  }, [d]);

  // Is this a V2 request (4-level HR chain + parallel CA) vs the legacy V1
  // single-HR-step chain?
  const isV2Workflow = String(d.workflow_version || "V1").toUpperCase() === "V2";

  // True exactly when it's the logged-in user's own current turn AS the HOD
  // HR (level 4) — distinct from canSendToCA below, which a HOD HR can use
  // pre-emptively before their own turn even arrives. At their own turn,
  // "Send to Competent Authority" replaces "Forward" entirely (see recOptions
  // and sendToCAAction) rather than being a second, separate action.
  const currentHrStep = Array.isArray(d.steps)
    ? d.steps.find((st: any) => st.is_hr && !st.is_ca && String(st.status || "").toUpperCase() === "CURRENT")
    : null;
  const isHodHrTurn =
    isV2Workflow &&
    !!currentHrStep &&
    String(currentHrStep.role || "").toUpperCase() === "HR_HOD" &&
    String(currentHrStep.approver_emp_id || "").trim().padStart(8, "0") ===
      String(getCurrentEmpNo() || "").trim().padStart(8, "0");

  // Level-agnostic on purpose — the CA-specific wording/action lives only in
  // the separate "Send to Competent Authority" button (HOD HR only), so this
  // stays a plain "Forward" at every HR level. Exception: at the HOD HR's own
  // turn, "Forward" is dropped — "Send to Competent Authority" does both. No
  // Reject option here — HR forwards or asks for clarification only.
  const recOptions: { key: Exclude<Recommendation, "">; label: string; icon: string; color: string }[] = isHodHrTurn
    ? [{ key: "clarify", label: "Clarification", icon: "ti-messages", color: "#185FA5" }]
    : [
        { key: "forward", label: "Forward", icon: "ti-arrow-forward-up", color: "#049123" },
        { key: "clarify", label: "Clarification", icon: "ti-messages", color: "#185FA5" },
      ];

  // True once every HR level has approved — for V1 this is the single routing
  // HR step (forwarded to the Competent Authority); for V2 it requires all 4
  // levels. Prefer the backend's explicit hr_chain_complete (generalizes both)
  // when present.
  const isHrAdminApproved = useMemo(() => {
    if (typeof d.hr_chain_complete === "boolean") return d.hr_chain_complete;
    if (!Array.isArray(d.steps)) return false;
    const hrStep = d.steps.find((st: any) => st.is_hr);
    return String(hrStep?.status || "").toUpperCase() === "APPROVED";
  }, [d]);

  // Service Certificate / Relieving Order readiness: for V1, unchanged
  // (isHrAdminApproved alone); for V2, the real 3-condition AND (mirrors the
  // backend's canGenerateFinalDocs).
  const canGenerateFinalDocs = isV2Workflow
    ? isHrAdminApproved && isCaApproved && !!noDuesSettled
    : isHrAdminApproved;

  // "Send to Competent Authority" is a one-time action available to the
  // configured HOD HR as soon as the request reaches HR — independent of
  // their own sequential HR-level-4 turn (they don't need to wait for levels
  // 1-3 to finish approving before dispatching to CA).
  const hasReachedHr = Array.isArray(d.steps) && d.steps.some((st: any) => st.is_hr);
  const canSendToCA =
    isV2Workflow &&
    hasReachedHr &&
    d.ca_approval?.status === "NOT_SENT" &&
    !!hodHrEmpId &&
    String(hodHrEmpId).trim().padStart(8, "0") ===
      String(getCurrentEmpNo() || "").trim().padStart(8, "0");

  const sendToCAAction = async () => {
    if (isHodHrTurn && !hrRemarks.trim()) {
      return toast.warning("Please enter HR remarks before forwarding.");
    }
    if (changeCA && !caPick) {
      return toast.warning("Search and select the Competent Authority, or cancel the change.");
    }
    const ok = await confirm(
      isHodHrTurn
        ? "Forward this request from your HR level and send it to the Competent Authority now?"
        : "Send this request to the Competent Authority now? This can be done at any time once the request has reached HR — it does not need to wait for all HR levels to finish.",
      { title: "Send to Competent Authority", confirmLabel: "Send", tone: "success" }
    );
    if (!ok) return;
    setSendingToCA(true);

    // At the HOD HR's own turn there's no separate "Forward" button (see
    // recOptions) — this single action both approves their HR-level step and
    // dispatches to CA.
    if (isHodHrTurn) {
      const approveRes = await submitApprovalAction({
        request_id: requestId,
        action: "Approve",
        remarks: hrRemarks,
        approver_role: "HR",
        approver_emp_no: "",
      });
      if (!approveRes.success) {
        setSendingToCA(false);
        toast.error(approveRes.message || "Failed to forward from your HR level.");
        return;
      }
    }

    const res = await submitSendToCA(requestId, changeCA && caPick ? caPick.emp_no : undefined);
    setSendingToCA(false);
    if (res.success) {
      toast.success(
        isHodHrTurn
          ? "Forwarded from your HR level and sent to the Competent Authority."
          : "Sent to the Competent Authority successfully."
      );
      navigate(routes.Esepration.hrVerification);
    } else {
      toast.error(res.message || "Failed to send to the Competent Authority.");
    }
  };

  const startEdit = () => {
    setForm({
      emp_name: d.emp_name || "",
      designation: d.designation || "",
      emp_type: d.emp_type || "",
      posting: d.posting || "",
      pay_scale: d.pay_scale || "",
      doj: toDateInput(d.doj),
      mobile: d.mobile || "",
      email: d.email || "",
      temp_address: d.temp_address || "",
      hometown: d.hometown || "",
      intimation_date: toDateInput(d.intimation_date),
      notice_end_date: toDateInput(d.notice_end_date),
      early_relieve: d.early_relieve === "Y" || d.early_relieve === true ? "Y" : "N",
      desired_relieve_date: toDateInput(d.desired_relieve_date),
      separation_type: d.separation_type || "",
      reason: d.reason || "",
      items_in_custody: d.items_in_custody === "Y" || d.items_in_custody === true ? "Y" : "N",
      items_in_custody_text: d.items_in_custody_text || "",
      co_name: d.co_name || "",
      co_mobile: d.co_mobile || "",
      noc_attached: d.noc_attached === "Y" || d.noc_attached === true ? "Y" : "N",
      noc_post: d.noc_post || "",
      noc_advt_no: d.noc_advt_no || "",
      noc_date: toDateInput(d.noc_date),
      qualification_details: d.qualification_details || "",
      temp_charge_officer: d.temp_charge_officer || "",
      training_bond: d.training_bond === "Y" || d.training_bond === true ? "Y" : "N",
      training_bond_details: d.training_bond_details || "",
      foreign_training_bond: d.foreign_training_bond === "Y" || d.foreign_training_bond === true ? "Y" : "N",
      foreign_training_period: d.foreign_training_period || "",
      foreign_training_bond_amount: Number(d.foreign_training_bond_amount) || 0,
      foreign_training_details: d.foreign_training_details || "",
      other_bond_submitted: d.other_bond_submitted === "Y" || d.other_bond_submitted === true ? "Y" : "N",
      other_bond_details: d.other_bond_details || "",
      // ccl_availed/ccl_period/lwp_availed/lwp_period are auto-fetched from
      // SAP (sap_lwp_records) — not part of HR's editable field set, same as
      // Outstanding Loans and Approval Hierarchy.
      eol_availed: d.eol_availed === "Y" || d.eol_availed === true ? "Y" : "N",
      eol_details: d.eol_details || "",
      withdraw_last_yr: d.withdraw_last_yr === "Y" || d.withdraw_last_yr === true ? "Y" : "N",
      withdraw_last_yr_details: d.withdraw_last_yr_details || "",
      deputation_availed: d.deputation_availed === "Y" || d.deputation_availed === true ? "Y" : "N",
      deputation_remittance_received:
        d.deputation_remittance_received === "Y" || d.deputation_remittance_received === true ? "Y" : "N",
      deputation_history: ((d.deputation_history || []) as any[]).length
        ? (d.deputation_history as any[]).map((r) => ({
            organization_name: r.organization_name || "",
            duration: r.duration || "",
          }))
        : [{ organization_name: "", duration: "" }],
      pending_cases_details: d.pending_cases_details || "",
      gratuity_transfer: d.gratuity_transfer === "Y" || d.gratuity_transfer === true ? "Y" : "N",
      // "Indicate Loan or Advances" (field 25) + any SAP-loan amount
      // corrections — both live in the same esep_loan rows, distinguished by
      // reference_number.
      loans: ((d.loans || []) as any[]).map((l) => ({
        loan_type: l.loan_type,
        amount: Number(l.amount) || 0,
        reference_number: l.reference_number ?? null,
        description: l.description ?? null,
      })),
      // "Your Sureties for Loans, if any" (field 26).
      loan_sureties: ((d.loan_sureties || []) as any[]).map((s) => ({
        loan_type: s.loan_type,
        surety_emp_id: s.surety_emp_id,
        surety_emp_name: s.surety_emp_name ?? null,
      })),
      bond_selections: ((d.bond_selections || []) as any[]).map((sel) => ({
        bond_type_id: sel.bond_type_id,
        bond_type_name: sel.bond_type_name,
        field_meta: sel.field_meta || [],
        values: { ...(sel.values || {}) },
      })),
    });
    setEditMode(true);
  };

  const cancelEdit = () => setEditMode(false);

  const setField = (key: string, value: any) => setForm((f) => ({ ...f, [key]: value }));

  const setDeputationRow = (idx: number, key: "organization_name" | "duration", value: string) =>
    setForm((f) => ({
      ...f,
      deputation_history: (f.deputation_history || []).map((r: any, i: number) =>
        i === idx ? { ...r, [key]: value } : r
      ),
    }));

  const addDeputationRow = () =>
    setForm((f) => ({
      ...f,
      deputation_history: [...(f.deputation_history || []), { organization_name: "", duration: "" }],
    }));

  const removeDeputationRow = (idx: number) =>
    setForm((f) => ({
      ...f,
      deputation_history: (f.deputation_history || []).filter((_: any, i: number) => i !== idx),
    }));

  const setBondFieldValue = (selIdx: number, fieldKey: string, value: any) =>
    setForm((f) => ({
      ...f,
      bond_selections: f.bond_selections.map((s: any, i: number) =>
        i === selIdx ? { ...s, values: { ...s.values, [fieldKey]: value } } : s
      ),
    }));

  // Finds the esep_loan row matching (loan_type, reference_number) in
  // form.loans and updates its amount, or appends a new one if it doesn't
  // exist yet (e.g. the employee left that field-25 category blank).
  const setLoanValue = (
    loan_type: string,
    referenceNumber: string | null,
    amount: string,
    description?: string
  ) =>
    setForm((f) => {
      const loans: any[] = f.loans || [];
      const idx = loans.findIndex(
        (l) => l.loan_type === loan_type && (l.reference_number ?? null) === referenceNumber
      );
      const amountNum = Number(amount) || 0;
      if (idx === -1) {
        return {
          ...f,
          loans: [
            ...loans,
            { loan_type, amount: amountNum, reference_number: referenceNumber, description: description ?? null },
          ],
        };
      }
      return {
        ...f,
        loans: loans.map((l, i) =>
          i === idx ? { ...l, amount: amountNum, description: description ?? l.description } : l
        ),
      };
    });

  const setLoanSuretyRow = (idx: number, key: "loan_type" | "surety_emp_id", value: string) =>
    setForm((f) => ({
      ...f,
      loan_sureties: (f.loan_sureties || []).map((r: any, i: number) =>
        i === idx ? { ...r, [key]: value, ...(key === "surety_emp_id" ? { surety_emp_name: null } : {}) } : r
      ),
    }));

  const addLoanSurety = () =>
    setForm((f) => ({
      ...f,
      loan_sureties: [...(f.loan_sureties || []), { loan_type: "", surety_emp_id: "" }],
    }));

  const removeLoanSurety = (idx: number) =>
    setForm((f) => ({
      ...f,
      loan_sureties: (f.loan_sureties || []).filter((_: any, i: number) => i !== idx),
    }));

  const handleSaveEdits = async () => {
    const ok = await confirm("Save these changes to the resignation request?", {
      title: "Save Changes",
      confirmLabel: "Save",
      tone: "success",
    });
    if (!ok) return;
    setSavingEdits(true);
    const res = await hrUpdateResignationRequest({
      request_id: requestId,
      emp_name: form.emp_name,
      designation: form.designation,
      emp_type: form.emp_type,
      posting: form.posting,
      pay_scale: form.pay_scale,
      doj: form.doj,
      mobile: form.mobile,
      email: form.email,
      temp_address: form.temp_address,
      hometown: form.hometown,
      intimation_date: form.intimation_date,
      notice_end_date: form.notice_end_date,
      early_relieve: form.early_relieve,
      desired_relieve_date: form.desired_relieve_date,
      separation_type: form.separation_type,
      reason: form.reason,
      items_in_custody: form.items_in_custody,
      items_in_custody_text: form.items_in_custody_text,
      co_name: form.co_name,
      co_mobile: form.co_mobile,
      noc_attached: form.noc_attached,
      noc_post: form.noc_post,
      noc_advt_no: form.noc_advt_no,
      noc_date: form.noc_date,
      qualification_details: form.qualification_details,
      temp_charge_officer: form.temp_charge_officer,
      training_bond: form.training_bond,
      training_bond_details: form.training_bond_details,
      foreign_training_bond: form.foreign_training_bond,
      foreign_training_period: form.foreign_training_period,
      foreign_training_bond_amount: form.foreign_training_bond_amount,
      foreign_training_details: form.foreign_training_details,
      other_bond_submitted: form.other_bond_submitted,
      other_bond_details: form.other_bond_details,
      eol_availed: form.eol_availed,
      eol_details: form.eol_details,
      withdraw_last_yr: form.withdraw_last_yr,
      withdraw_last_yr_details: form.withdraw_last_yr_details,
      deputation_availed: form.deputation_availed,
      deputation_remittance_received: form.deputation_remittance_received,
      deputation_history: (form.deputation_history || []).filter((r: any) => r.organization_name?.trim()),
      pending_cases_details: form.pending_cases_details,
      gratuity_transfer: form.gratuity_transfer,
      loans: (form.loans || []).filter((l: any) => Number(l.amount) > 0),
      // loan_sureties carries surety_emp_name for local display (seeded from
      // the read response / set on select) — the backend's LoanSuretyItemDto
      // only accepts {loan_type, surety_emp_id} and rejects extra properties.
      loan_sureties: (form.loan_sureties || [])
        .filter((s: any) => s.surety_emp_id?.trim())
        .map((s: any) => ({ loan_type: s.loan_type, surety_emp_id: s.surety_emp_id })),
      bond_selections: form.bond_selections.map((s: any) => ({
        bond_type_id: s.bond_type_id,
        values: s.values,
      })),
    });
    if (res.success) {
      toast.success("Request updated successfully.");
      const refreshed = await getResignationDetails(requestId);
      if (refreshed.data) setDetails(refreshed.data);
      setEditMode(false);
    } else {
      toast.error(res.message || "Failed to update request.");
    }
    setSavingEdits(false);
  };

  const handleDownload = async (
    docType:
      | "acceptance_letter"
      | "service_certificate"
      | "relieving_letter"
      | "no_dues_certificate_doc",
    docName: string,
    available: boolean
  ) => {
    if (!available) {
      toast.warning(`${docName} is not yet available.`);
      return;
    }
    setDownloading((p) => ({ ...p, [docType]: true }));
    let res;
    switch (docType) {
      case "acceptance_letter":
        res = await generateAcceptanceLetter(requestId);
        break;
      case "service_certificate":
        res = await generateServiceCertificate(requestId);
        break;
      case "relieving_letter":
        res = await generateRelievingOrder(requestId);
        break;
      case "no_dues_certificate_doc":
        res = await generateNoDuesCertificate(requestId);
        break;
    }
    setDownloading((p) => ({ ...p, [docType]: false }));
    if (res.success) toast.success(`${docName} downloaded successfully.`);
    else toast.error(res.message || `Failed to download ${docName}.`);
  };

  const handleSendToVigilance = async () => {
    const ok = await confirm(
      vigilanceCase
        ? "Re-send this request to the vigilance officer? Any prior remarks will be cleared."
        : "Send this resignation request to the vigilance officer for clearance?",
      { title: "Send to Vigilance", confirmLabel: "Send", tone: "success" }
    );
    if (!ok) return;
    setSendingToVigilance(true);
    const res = await sendToVigilance(requestId);
    setSendingToVigilance(false);
    const body: any = res.data;
    if (res.success || body?.id || body?.vigilance_case_id) {
      toast.success("Sent to Vigilance successfully.");
      setVigilanceCase(body ?? null);
    } else {
      toast.error(res.message || "Failed to send to Vigilance.");
    }
  };

  const forwardToCA = async () => {
    if (!hrRemarks.trim()) return toast.warning("Please enter HR remarks before forwarding.");
    if (recommendation !== "forward") return;
    // V2: this approves whichever of the 4 HR levels the current step is,
    // uniformly — never forwards to CA itself, regardless of level (that's
    // the separate, independent "Send to Competent Authority" action above,
    // HOD HR only). V1 keeps its original single-HR-step wording unchanged.
    const ok = await confirm(
      isV2Workflow
        ? "Forward this request from your HR level?"
        : "Forward this request to the Competent Authority for final approval?",
      { title: "Confirm Forward", confirmLabel: "Forward", tone: "success" }
    );
    if (!ok) return;
    setSubmitting(true);
    const res = await submitApprovalAction({
      request_id: requestId,
      action: "Approve",
      remarks: hrRemarks,
      approver_role: "HR",
      approver_emp_no: "",
    });
    setSubmitting(false);
    if (res.success) {
      toast.success(isV2Workflow ? "Forwarded from your HR level." : "Request forwarded to Competent Authority.");
      navigate(routes.Esepration.hrVerification);
    } else {
      toast.error(res.message || "Failed to forward. Please try again.");
    }
  };

  const sendClarification = async () => {
    if (!hrRemarks.trim()) return toast.warning("Please enter clarification query in remarks.");
    if (recommendation !== "clarify") return;
    const ok = await confirm(
      "Send this request back to the employee for clarification?",
      { title: "Confirm Clarification", confirmLabel: "Send Query", tone: "warning" }
    );
    if (!ok) return;
    setSubmitting(true);
    const res = await raiseClarification({
      request_id: requestId,
      query_string: hrRemarks.trim(),
    });
    setSubmitting(false);
    if (res.success) {
      toast.success("Clarification query sent to employee.");
      navigate(routes.Esepration.hrVerification);
    } else {
      toast.error(res.message || "Failed to send clarification query. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
            <p className="text-muted mt-3 mb-0">Loading Request Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!requestId || !details) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="fw-bold text-dark mb-0">HR Verification</h4>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Back
            </button>
          </div>
          <WarningBanner
            type="warning"
            message="Could not load this request. Please go back and try again."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hrVerification);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    HR Verification
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Verify Request
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">{fmt(d.emp_name)}</h4>
            <span className="text-muted small">
              {requestId} • {fmt(d.emp_no)}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {canEdit && !editMode && (
              <button className="btn btn-outline-secondary btn-sm" onClick={startEdit}>
                <i className="ti ti-edit me-1" />
                Edit
              </button>
            )}
            {editMode && (
              <>
                <button className="btn btn-outline-secondary btn-sm" onClick={cancelEdit} disabled={savingEdits}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveEdits}
                  disabled={savingEdits}
                >
                  {savingEdits ? (
                    <span className="spinner-border spinner-border-sm me-1" />
                  ) : (
                    <i className="ti ti-device-floppy me-1" />
                  )}
                  Save Changes
                </button>
              </>
            )}
            <ApproverRoleBadge role="HR" />
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Back
            </button>
          </div>
        </div>

        {/* Request Type Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 44, height: 44, borderRadius: 10, background: "#e8f4fd", color: "#0d6efd" }}
                >
                  <i className="ti ti-file-text" style={{ fontSize: 22 }} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#344054",
                      lineHeight: "1.3",
                    }}
                  >
                    {d.request_type || "Intimation & Resignation"}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#667085",
                      lineHeight: "1.4",
                      marginTop: "2px",
                    }}
                  >
                    Verify employee details, D&amp;AR &amp; Vigilance clearance, and forward to CA
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {/* A short notice is a request of its own that replaces the original
                    resignation — let HR open that original form as filed. */}
                {String(d.request_type || "").toUpperCase() === "SHORT_NOTICE" && d.orig_request_id && (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() =>
                      navigate(routes.Esepration.approvalDetail.replace(":id", d.orig_request_id), {
                        state: {
                          request_id: d.orig_request_id,
                          type: "Intimation",
                          role: "Employee",
                          viewOnly: true,
                        },
                      })
                    }
                  >
                    <i className="ti ti-file-text me-1" />
                    View Original Resignation
                  </button>
                )}
                <StatusBadge status={d.status || "REACHED_HR"} />
              </div>
            </div>
          </div>
        </div>

        {/* The same 7 sections, labels and values the employee filled in on
            their own resignation form — identical to the approver's view,
            with the sole difference that HR can toggle Edit on top of it. */}
        <ResignationFormSections
          d={formRecord}
          mode={editMode ? "edit" : "view"}
          form={form}
          setField={setField}
          setBondFieldValue={setBondFieldValue}
          setDeputationRow={setDeputationRow}
          addDeputationRow={addDeputationRow}
          removeDeputationRow={removeDeputationRow}
          setLoanValue={setLoanValue}
          setLoanSuretyRow={setLoanSuretyRow}
          addLoanSurety={addLoanSurety}
          removeLoanSurety={removeLoanSurety}
          empMaster={empMaster}
        />

        {/* SECTION 08: VIGILANCE */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                08
              </span>
              Vigilance
            </h5>
            {vigilanceCase && <StatusBadge status={vigilanceCase.status} />}
          </div>
          <div className="card-body p-4">
            <p className="text-muted small mb-3">
              Send this resignation to the vigilance officer as an informational
              side-check. This does not block forwarding the request to the
              Competent Authority.
            </p>

            {vigilanceCase && (
              <div className="row g-3 mb-3">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Clearance Status</label>
                  <div className="pt-1">
                    <StatusBadge status={vigilanceCase.status} />
                  </div>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Vigilance Officer</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={fmt(vigilanceCase.officer_emp_id)}
                    readOnly
                  />
                </div>

                {vigilanceCase.pending_case_ref && (
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Pending Case Ref.</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={fmt(vigilanceCase.pending_case_ref)}
                      readOnly
                    />
                  </div>
                )}

                {vigilanceCase.dispatched_at && (
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Sent On</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={formatDateLong(vigilanceCase.dispatched_at)}
                      readOnly
                    />
                  </div>
                )}

                {vigilanceCase.acted_at && (
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Actioned On</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={formatDateLong(vigilanceCase.acted_at)}
                      readOnly
                    />
                  </div>
                )}

                {vigilanceCase.remarks && (
                  <div className="col-12 mt-2">
                    <label className="form-label fw-semibold text-secondary small mb-1">Remarks</label>
                    <div
                      className="p-3 rounded-3 bg-light text-dark small"
                      style={{ border: "1px solid #e9ecef" }}
                    >
                      {vigilanceCase.remarks}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <button
                className="btn btn-outline-primary"
                onClick={handleSendToVigilance}
                disabled={sendingToVigilance}
              >
                {sendingToVigilance ? (
                  <span className="spinner-border spinner-border-sm me-2" />
                ) : (
                  <i className="ti ti-send me-1" />
                )}
                {vigilanceCase ? "Re-send to Vigilance" : "Send to Vigilance"}
              </button>
            </div>
          </div>
        </div>

        {/* SECTION 09: APPROVER REMARK SUMMARY */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                09
              </span>
              Approver Remark Summary
            </h5>
          </div>
          <div className="card-body p-4">
            <p className="text-muted small mb-3">
              Consolidated approver remarks from all prior approval levels.
            </p>
            {dar.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {dar.map((item, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-3 border bg-light"
                    style={{ borderColor: "#e9ecef" }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-bold text-dark">{formatDarLevel(item.level)}</span>
                      <StatusBadge status={item.dar_status} />
                    </div>
                    <small className="text-muted d-block">
                      {item.officer_name} ({item.officer_emp_no})
                    </small>
                    {item.remarks && (
                      <div className="fst-italic small mt-2 text-secondary bg-white p-2 rounded border">
                        "{item.remarks}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <WarningBanner
                type="info"
                message="D&AR remarks will appear here once prior approvers take action."
              />
            )}
          </div>
        </div>

        {/* CLARIFICATIONS — only the conversation between the viewer and the
            employee (the server returns just the viewer's own thread), kept
            separate from the approver remark summary above. */}
        {Array.isArray((details as any)?.clarifications) &&
          (details as any).clarifications.length > 0 && (
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    <i className="ti ti-messages" />
                  </span>
                  Clarifications
                </h5>
              </div>
              <div className="card-body p-4">
                <ClarificationThread clarifications={(details as any).clarifications} />
              </div>
            </div>
          )}

        {/* SECTION 10: D&AR DETAILS */}
        {(details as any)?.dar_details && (() => {
          const dDar: DarDetails = (details as any).dar_details;
          const statusColor: Record<string, string> = {
            CLEARED: "#0F6E56",
            PENDING: "#BA7517",
            HOLD: "#C0282C",
            NOT_APPLICABLE: "#888",
          };
          return (
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    10
                  </span>
                  D&amp;AR Details
                </h5>
              </div>
              <div className="card-body p-4">
                <p className="text-muted small mb-3">
                  Disciplinary &amp; Administrative Records details reported by the forwarding approver.
                </p>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Any D&amp;AR Case Pending
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={dDar.dar_case_pending === "Y" ? "Yes" : "No"}
                      readOnly
                    />
                  </div>

                  {dDar.dar_case_pending === "Y" && (
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Case Reference No.
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={fmt(dDar.dar_case_no)}
                        readOnly
                      />
                    </div>
                  )}

                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Clearance Status
                    </label>
                    <div className="pt-1">
                      <span
                        className="badge px-2 py-1"
                        style={{
                          fontWeight: 600,
                          background: `${statusColor[dDar.dar_status || ""] || "#888"}20`,
                          color: statusColor[dDar.dar_status || ""] || "#888",
                        }}
                      >
                        {(dDar.dar_status || "—").replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Reported By
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={`${dDar.filled_by_emp_id || "—"}${dDar.filled_by_role ? ` (${dDar.filled_by_role})` : ""}`}
                      readOnly
                    />
                  </div>

                  {dDar.filled_at && (
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Reported At
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formatDate(dDar.filled_at)}
                        readOnly
                      />
                    </div>
                  )}

                  {dDar.dar_remarks && (
                    <div className="col-12 mt-2">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        D&amp;AR Remarks
                      </label>
                      <div
                        className="p-3 rounded-3 bg-light text-dark small"
                        style={{ border: "1px solid #e9ecef" }}
                      >
                        {dDar.dar_remarks}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* SECTION 11: D&AR CASE HISTORY (real module records) */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                11
              </span>
              D&amp;AR Case History
            </h5>
          </div>
          <div className="card-body p-4">
            <DarCaseHistorySection cases={(details as any)?.dar_module_cases} />
          </div>
        </div>

        {/* SECTION 12: BOND & FINANCIAL VERIFICATION */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                12
              </span>
              Bond &amp; Financial Verification
            </h5>
          </div>
          <div className="card-body p-4">
            {/* Training Bond Status Subcard */}
            <div className="card border mb-3" style={{ borderRadius: 10 }}>
              <div className="card-header bg-light py-2 px-3">
                <h6 className="mb-0 fw-semibold text-dark d-flex align-items-center gap-2">
                  <i className="ti ti-school text-warning" />
                  Training Bond Status
                </h6>
              </div>
              <div className="card-body p-3">
                <div className="row g-3">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">Under Bond</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={bondData.under_bond ? "Yes" : "No"}
                      readOnly
                    />
                  </div>

                  {bondData.under_bond && (
                    <>
                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">Bond Period</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={fmt(bondData.bond_period)}
                          readOnly
                        />
                      </div>

                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Training Expenditure
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={formatINR(bondData.training_expenditure)}
                          readOnly
                        />
                      </div>

                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Pending Recoveries
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light text-danger fw-bold"
                          value={formatINR(bondData.pending_recoveries)}
                          readOnly
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* No-Dues real-time status — read-only for every HR level; only the
            current HR step's own occupant (Sr. CRA on the V2 flow) can act on
            the No-Dues itself, from the Approvals inbox. */}
        {nodDuesRequestId && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-warning text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  <i className="ti ti-building-bank" style={{ fontSize: 16 }} />
                </span>
                No-Dues Status ({nodDuesRequestId})
              </h5>
              {noDuesSettled && <span className="badge bg-success-transparent text-success">All Cleared</span>}
            </div>
            <div className="card-body p-4">
              {noDuesDispatchRows.length === 0 ? (
                <p className="text-muted mb-0">
                  Not yet dispatched to departments by the HR Assistant.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="text-muted small text-uppercase">
                        <th>Department</th>
                        <th>Approver</th>
                        <th>Status</th>
                        <th>Recovery Amount</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {noDuesDispatchRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.department_name}</td>
                          <td>{row.delegated_to_name || row.approver_emp_id || "—"}</td>
                          <td>
                            <StatusBadge status={row.status} />
                          </td>
                          <td>{row.recovery_amount ? formatINR(row.recovery_amount) : "—"}</td>
                          <td className="text-truncate" style={{ maxWidth: 220 }}>
                            {row.remarks || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECTIONS 13-16: the employee's own submitted No-Dues form,
            rendered identically to how they filled it in. Only meaningful
            once a No-Dues has actually been raised. */}
        {nodDuesRequestId && <NoDuesFormSections form={ndForm} startNumber={13} />}

        {/* SECTION 13/17: DOCUMENTS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                {nodDuesRequestId ? "17" : "13"}
              </span>
              Documents
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              {[
                { docType: "acceptance_letter" as const, docName: "Acceptance Letter", available: isCaApproved },
                { docType: "service_certificate" as const, docName: "Service Certificate", available: canGenerateFinalDocs && !!d.office_order_no },
                { docType: "relieving_letter" as const, docName: "Relieving Order", available: canGenerateFinalDocs && !!d.office_order_no },
                {
                  docType: "no_dues_certificate_doc" as const,
                  docName: "No Dues Certificate",
                  available: !!nodDuesRequestId,
                },
              ].map((doc) => (
                <div className="col-md-3 col-sm-6" key={doc.docType}>
                  <button
                    type="button"
                    className="card border-0 shadow-sm w-100 h-100 text-center p-3"
                    style={{ borderRadius: 14, opacity: doc.available ? 1 : 0.6, cursor: "pointer" }}
                    onClick={() => handleDownload(doc.docType, doc.docName, doc.available)}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center mx-auto mb-2"
                      style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: doc.available ? "#E6F1FB" : "rgba(0,0,0,0.05)",
                        color: doc.available ? "#185FA5" : "#9aa0a6",
                      }}
                    >
                      {downloading[doc.docType] ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        <i className="ti ti-file-download" style={{ fontSize: 20 }} />
                      )}
                    </span>
                    <div className="fw-semibold small">{doc.docName}</div>
                    {doc.available ? (
                      <span
                        className="d-inline-flex align-items-center gap-1 mt-2 px-2 py-1 rounded-pill mx-auto"
                        style={{ background: "#E6F1FB", color: "#185FA5", fontSize: 11 }}
                      >
                        <i className="ti ti-download" /> PDF
                      </span>
                    ) : (
                      <span className="text-muted mt-2 d-block" style={{ fontSize: 11 }}>
                        Pending
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SECTION 14: HR RECOMMENDATION */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                {nodDuesRequestId ? "18" : "14"}
              </span>
              HR Recommendation
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="mb-3">
              <label className="form-label fw-semibold text-secondary small mb-1">
                HR Remarks <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="Enter your verification remarks..."
                value={hrRemarks}
                onChange={(e) => setHrRemarks(e.target.value)}
              />
            </div>

            {!isCaApproved && (
              <div className="mb-3">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Recommendation <span className="text-danger">*</span>
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {recOptions.map((opt) => {
                    const active = recommendation === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        className="btn text-start d-flex align-items-center gap-2 px-3 py-2"
                        style={{
                          border: `1px solid ${active ? opt.color : "rgba(0,0,0,0.15)"}`,
                          background: active ? `${opt.color}15` : "#fff",
                          borderRadius: 10,
                        }}
                        onClick={() => setRecommendation(opt.key)}
                      >
                        <i className={`ti ${opt.icon} fs-5`} style={{ color: opt.color }} />
                        <span
                          style={{
                            color: active ? opt.color : "#344054",
                            fontWeight: active ? 600 : 500,
                          }}
                        >
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* The Competent Authority this request goes to. The HOD HR may pick
                someone other than the configured one before sending. */}
            {canSendToCA && (
              <div className="mb-1 p-3 rounded-3" style={{ background: "#F6F8FB", border: "1px solid rgba(0,0,0,0.08)" }}>
                <label className="form-label fw-semibold text-secondary small mb-1">Competent Authority</label>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span className="fw-semibold">
                    {d.ca_approval?.approver_name || "—"}{" "}
                    <span className="text-muted fw-normal">({d.ca_approval?.approver_emp_id || "—"})</span>
                  </span>
                  {!changeCA ? (
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => setChangeCA(true)}>
                      <i className="ti ti-edit me-1" />
                      Change
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary ms-auto"
                      onClick={() => {
                        setChangeCA(false);
                        setCaPick(null);
                        setCaQuery("");
                      }}
                    >
                      Use the configured CA
                    </button>
                  )}
                </div>
                {changeCA && (
                  <div className="mt-3">
                    <EmployeeSearchInput
                      value={caQuery}
                      onChange={(v) => {
                        setCaQuery(v);
                        setCaPick(null);
                      }}
                      onSelect={(item) => {
                        setCaPick(item);
                        setCaQuery(`${item.emp_no} — ${item.emp_name}`);
                      }}
                      placeholder="Search the new Competent Authority by employee number or name…"
                    />
                    {caPick && (
                      <div className="small text-success mt-2">
                        <i className="ti ti-circle-check me-1" />
                        The request will go to {caPick.emp_name} ({caPick.emp_no}) instead of the configured Competent Authority.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>


      </div>

      {/* Sticky Bottom Action Bar */}
      {!isCaApproved && (
        <div
          className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg py-3 px-4 d-flex align-items-center justify-content-end gap-2"
          style={{ zIndex: 1000 }}
        >
          {/* V2 only: independent of the sequential HR-level approval below —
              available to the configured HOD HR as soon as the request
              reaches HR, whether or not levels 1-3 have finished. */}
          {canSendToCA && (
            <button
              className="btn btn-outline-success d-flex align-items-center gap-2 px-4 py-2"
              onClick={sendToCAAction}
              disabled={sendingToCA}
            >
              {sendingToCA ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <i className="ti ti-send" />
              )}
              <span>Send to Competent Authority</span>
            </button>
          )}
          {/* At the HOD HR's own turn, "Forward" is dropped entirely — "Send
              to Competent Authority" above does both. Only Clarification
              still uses this button then. */}
          {(!isHodHrTurn || recommendation === "clarify") && (
            <button
              className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2"
              style={{
                background: recommendation === "clarify" ? "#185FA5" : "#049123",
                borderColor: recommendation === "clarify" ? "#185FA5" : "#049123",
              }}
              onClick={recommendation === "clarify" ? sendClarification : forwardToCA}
              disabled={!hrRemarks.trim() || !recommendation || submitting}
            >
              {submitting ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <i className={`ti ${recommendation === "clarify" ? "ti-messages" : "ti-arrow-forward-up"}`} />
              )}
              <span>
                {recommendation === "clarify"
                  ? "Send for Clarification"
                  : isV2Workflow
                  ? "Forward"
                  : "Forward to Competent Authority for Approval"}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default HRVerificationDetail;
